import { Box, ButtonBase } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import type { ReactNode } from 'react';

const CHEVRON_PX = 18;
const CHEVRON_BOX_PX = 20;
/** The chevron stands this far into the row's right padding. */
const CHEVRON_OVERHANG_PX = 2;

/**
 * The picker box's control: it names the image on its first line and the
 * structure set drawn on it on the second, and opens the dataset menu.
 * While the menu is open it is filled, and its chevron turns teal and up.
 */
export default function DatasetButton({
    first,
    second,
    open,
    onClick,
    'data-testid': testId,
}: {
    first: ReactNode;
    second: ReactNode;
    /** Whether the dataset menu is open. */
    open: boolean;
    onClick: () => void;
    'data-testid'?: string;
}) {
    return (
        <ButtonBase
            onClick={onClick}
            disableRipple
            aria-haspopup="menu"
            aria-expanded={open}
            data-testid={testId}
            sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 0.5,
                minWidth: 0,
                // Takes the width its container gives it: a long name is
                // cut short and never widens the container.
                contain: 'inline-size',
                p: 1,
                textAlign: 'left',
                font: 'inherit',
                color: 'text.primary',
                backgroundColor: open
                    ? theme.palette.action.selected
                    : 'transparent',
                transition: theme.transitions.create('background-color', {
                    duration: theme.transitions.duration.short,
                }),
                '&:hover': {
                    backgroundColor: open
                        ? theme.palette.action.selected
                        : theme.palette.action.hover,
                },
                '&.Mui-focusVisible': {
                    backgroundColor: theme.palette.action.hover,
                },
            })}
        >
            {/* The chevron's glyph sits inside its box, so a small gap
                already reads as the space before it. */}
            <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                <Box
                    display="flex"
                    alignItems="center"
                    gap="10px"
                    flex={1}
                    minWidth={0}
                >
                    {first}
                </Box>
                <Box
                    component="span"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    width={CHEVRON_BOX_PX}
                    height={CHEVRON_BOX_PX}
                    mr={`${-CHEVRON_OVERHANG_PX}px`}
                    color={open ? 'primary.main' : 'text.secondary'}
                    sx={(theme) => ({
                        transition: theme.transitions.create('color', {
                            duration: theme.transitions.duration.short,
                        }),
                    })}
                >
                    <ExpandMore
                        sx={(theme) => ({
                            fontSize: CHEVRON_PX,
                            // Turns while the menu is open, so the row
                            // says which way its menu went.
                            transform: open ? 'rotate(180deg)' : 'none',
                            transition: theme.transitions.create('transform', {
                                duration: theme.transitions.duration.short,
                            }),
                        })}
                    />
                </Box>
            </Box>
            <Box
                display="flex"
                alignItems="center"
                gap="6px"
                minWidth={0}
                fontSize={11}
                color="text.secondary"
                whiteSpace="nowrap"
            >
                {second}
            </Box>
        </ButtonBase>
    );
}
